import { useState } from "react";
import { useOutletContext, useSearchParams } from "react-router-dom";

import type { ShellOutletContext } from "../nav/AppShell";
import { AdminSectionHead } from "../components/AdminSectionHead";
import { FeedComposer } from "../surfaces/FeedComposer";
import { FeedPostList } from "../surfaces/FeedPostList";
import { fetchClientSiteSetup, type ClientSiteSetup } from "../data/clients-sites";
import {
  FEED_MANAGE_PERMISSION,
  fetchFeedPosts,
  feedAudienceOptions,
  type FeedLoad,
} from "../data/feed";
import { ALL_COMPANY_AUDIENCE } from "../data/feed";
import { useReadModel } from "../data/useReadModel";

// Feed (product lock 2026-08-07 §2, design draft 1 plates 04/05). A bulletin surface: supervisors
// post, the company reads, and some posts must be acknowledged. Deliberately its own page rather
// than another Admin section - authoring plus a post history is a working surface, not a settings
// row, and Admin is already long.
//
// Reads are ungated (any console user is management staff; guards never sign in here). Authoring, pin,
// require-ack, and the ack roster are gated on feed.manage.

export function FeedPage({ embedded = false }: { readonly embedded?: boolean }) {
  const { client, config, can } = useOutletContext<ShellOutletContext>();
  const [searchParams] = useSearchParams();
  const canManage = can(FEED_MANAGE_PERMISSION);
  const [reloadNonce, setReloadNonce] = useState(0);

  const feed = useReadModel<FeedLoad>(
    () => fetchFeedPosts(client, config.accountId),
    [client, config.accountId, reloadNonce],
    { pollMs: config.refreshMs },
  );

  // The site list only exists to fill the audience picker. If it fails, the composer still works
  // for All company rather than the whole page dying over an optional dropdown.
  const sites = useReadModel<ClientSiteSetup>(
    () => fetchClientSiteSetup(client, config.accountId),
    [client, config.accountId],
  );

  const audienceOptions =
    sites.data === null ? [ALL_COMPANY_AUDIENCE] : feedAudienceOptions(sites.data.sites, sites.data.clients);
  const audienceNote =
    sites.status === "error" ? "Site list unavailable, so this post can only go to the whole company." : null;

  return (
    <div className={embedded ? "feed-page inbox-host-page" : "page feed-page"}>
      {embedded ? (
        <h2 className="page__title">Action / Feed</h2>
      ) : (
        <h1 className="page__title">Feed</h1>
      )}
      <p className="page__note">
        Company and site bulletins. Posts appear on every guard's phone; posts that require
        acknowledgement stay outstanding until each person opens and confirms them.
      </p>

      {canManage ? (
        <section id="new-feed-post" className="admin__section" aria-label="New post">
          <AdminSectionHead
            title="New post"
            scope="account"
            hint="Title, body, and who it goes to. Pin keeps it at the top; require acknowledgement makes each person confirm they read it."
          />
          <FeedComposer
            client={client}
            accountId={config.accountId}
            audienceOptions={audienceOptions}
            audienceNote={audienceNote}
            onPublished={() => setReloadNonce((nonce) => nonce + 1)}
          />
        </section>
      ) : null}

      <section className="admin__section" aria-label="Posts">
        <AdminSectionHead
          title="Posts"
          scope="account"
          hint={
            canManage
              ? "Newest first, with pinned posts on top. Open a required post to see who has acknowledged it."
              : "Newest first, with pinned posts on top."
          }
        />
        <FeedBody
          load={feed.data}
          status={feed.status}
          error={feed.error}
          onChanged={() => setReloadNonce((nonce) => nonce + 1)}
          canManage={canManage}
          client={client}
          accountId={config.accountId}
          timeZone={config.displayTimeZone}
          selectedPostId={searchParams.get("post")}
        />
      </section>
    </div>
  );
}

function FeedBody({
  load,
  status,
  error,
  onChanged,
  canManage,
  client,
  accountId,
  timeZone,
  selectedPostId,
}: {
  readonly load: FeedLoad | null;
  readonly status: "loading" | "ready" | "error";
  readonly error: string | null;
  readonly onChanged: () => void;
  readonly canManage: boolean;
  readonly client: ShellOutletContext["client"];
  readonly accountId: string;
  readonly timeZone: string;
  readonly selectedPostId: string | null;
}) {
  if (status === "loading" && load === null) {
    return (
      <p className="surface__state" role="status">
        Loading the Feed...
      </p>
    );
  }
  if (status === "error") {
    return (
      <p className="surface__state surface__state--error" role="alert">
        {error ?? "Could not load the Feed."}
      </p>
    );
  }
  if (load === null) return null;

  // Three distinct states. "Not deployed" is not "no posts": only one of them means the company is
  // up to date.
  if (load.status === "unavailable" || load.status === "failed") {
    return (
      <p
        className={load.status === "failed" ? "surface__state surface__state--error" : "surface__state"}
        role={load.status === "failed" ? "alert" : "status"}
      >
        {load.reason}
      </p>
    );
  }

  // Deliberately NOT the shared outstandingAckCount: `ackedAt` is the *reading member's* own
  // acknowledgement, which is the guard's question ("what do I still owe?"). A supervisor on the
  // console is asking a different one, so this counts posts that require an acknowledgement and
  // sends them to the per-post roster for who is outstanding.
  const requiring = load.posts.filter((post) => post.requiresAck).length;

  return (
    <>
      {requiring > 0 ? (
        <p className="feed-page__outstanding" role="status">
          {requiring === 1
            ? "1 post requires acknowledgement. Open it to see who is outstanding."
            : `${requiring} posts require acknowledgement. Open one to see who is outstanding.`}
        </p>
      ) : null}
      <FeedPostList
        client={client}
        accountId={accountId}
        posts={load.posts}
        timeZone={timeZone}
        canManage={canManage}
        onChanged={onChanged}
        selectedPostId={selectedPostId}
      />
    </>
  );
}
